/**
 * ONE place that turns a saved/edited HTTP request into what goes on the wire.
 *
 * Send (RequestBuilder) and Run collection (collectionRunner) used to build the
 * outgoing request with two hand-kept copies of the same logic, and they drifted:
 * the runner applied `collection.auth` (a field nothing writes — the collection's
 * auth lives in `settings.collection_auth`), sent GraphQL as a raw string with
 * the variables still a string and only for POST/PUT/PATCH, ignored the request's
 * cookie rows, and Send never walked the folder auth chain the runner did.
 * Card 77f785cc (26.09.2026). Both callers now go through here, so they cannot
 * disagree again; `src/tests/requestAssemblyParity.test.js` pins it.
 *
 * Pure apart from `applyRequestAuth` (async for AWS SigV4). No store, no Tauri.
 */
import { applyRequestAuth, resolveEffectiveAuthChain } from './requestAuth';
import { buildMultipartParts } from './multipart';
import { buildRequestUrl } from './urlParams';
import {
  normalizeRequestBodyMode,
  contentTypeForRawLanguage,
  isGraphQLBodyMode,
  isNoBodyMode,
  isUrlencodedBodyMode,
} from './requestBodyMode';
import { normalizeGraphQLBody } from './graphqlSchema';

const identity = (s) => s;
const BODY_METHODS = ['POST', 'PUT', 'PATCH'];

/** The collection-level auth block, wherever this collection object keeps it. */
export function collectionAuthOf(collection) {
  return collection?.settings?.collection_auth ?? collection?.auth ?? null;
}

/**
 * GraphQL body → JSON wire string. `{{vars}}` are substituted inside the query,
 * the variables JSON and the operation name, THEN the variables are parsed, so
 * `{"id": {{userId}}}` becomes a real number. Variables that still do not parse
 * are sent as the string the user wrote (the server gets to say why).
 */
export function encodeGraphQLBody(body, substituteVariables = identity) {
  const sub = (v) => substituteVariables(String(v ?? ''));
  const gql = normalizeGraphQLBody(body);
  const payload = { query: sub(gql.query) };
  const rawVariables = gql.variables ? sub(gql.variables) : '';
  if (rawVariables.trim()) {
    try {
      payload.variables = JSON.parse(rawVariables);
    } catch {
      payload.variables = rawVariables;
    }
  }
  const operationName = sub(gql.operationName);
  if (operationName) payload.operationName = operationName;
  return JSON.stringify(payload);
}

function enabledRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((r) => r && r.key && r.enabled !== false);
}

/**
 * Body for the request's mode. Returns `{ body, multipart, contentType }`;
 * `contentType` null means "leave the headers alone".
 */
export function assembleRequestBody(
  { method, bodyType, rawBodyLanguage, body, formDataFields },
  { substituteVariables = identity } = {}
) {
  const upper = String(method || 'GET').toUpperCase();
  const sub = substituteVariables;
  const fields = Array.isArray(formDataFields) ? formDataFields : [];

  if (isUrlencodedBodyMode(bodyType)) {
    const encoded = fields
      .filter((f) => f && f.enabled !== false && String(f.key ?? '').trim() && f.type !== 'file')
      .map((f) => `${encodeURIComponent(sub(f.key))}=${encodeURIComponent(sub(f.value ?? ''))}`)
      .join('&');
    return { body: encoded, multipart: null, contentType: 'application/x-www-form-urlencoded' };
  }
  if (isGraphQLBodyMode(bodyType)) {
    // GraphQL is always JSON, whatever the method picker says.
    return { body: encodeGraphQLBody(body, sub), multipart: null, contentType: 'application/json' };
  }
  if (isNoBodyMode(bodyType) || !BODY_METHODS.includes(upper)) {
    return { body: null, multipart: null, contentType: null };
  }
  if (bodyType === 'form-data') {
    const parts = buildMultipartParts(fields, sub).map((part) => {
      if (part.file_path) return part;
      // A file picked in THIS session is a browser File on the row, not a path.
      const row = fields.find((r) => r && r.enabled !== false && sub(r.key).trim() === part.name);
      if (row?.file) return { name: part.name, file: row.file, file_name: row.fileName || row.file.name };
      return part;
    });
    return { body: null, multipart: parts, contentType: null };
  }
  if (!body) return { body: null, multipart: null, contentType: null };

  // Sent exactly as typed: re-serialising JSON rounded integers above 2^53 and
  // broke any signature a pre-request script computed (card f0432ea2).
  const resolved = sub(typeof body === 'string' ? body : JSON.stringify(body));
  return { body: resolved, multipart: null, contentType: contentTypeForRawLanguage(rawBodyLanguage) };
}

/**
 * Everything except auth: method, URL (+ enabled params), headers (+ default
 * User-Agent, + cookie rows), body / multipart parts.
 *
 * `request` is the editor/sidebar shape: { method, url, params, headers,
 * cookies, body, bodyType|body_type, rawBodyLanguage, settings, formDataFields }.
 */
export function assembleRequestParts(request, { substituteVariables = identity } = {}) {
  const sub = substituteVariables;
  const method = String(request?.method || 'GET').toUpperCase();

  const headers = {};
  for (const h of enabledRows(request?.headers)) {
    headers[sub(h.key)] = sub(h.value ?? '');
  }
  // Some APIs (GitHub) 403 without one; the user's own, any casing, wins.
  if (!Object.keys(headers).some((k) => k.toLowerCase() === 'user-agent')) {
    headers['User-Agent'] = 'Apiboo/1.0';
  }
  const cookies = enabledRows(request?.cookies);
  if (cookies.length > 0) {
    headers.Cookie = cookies.map((c) => `${sub(c.key)}=${sub(c.value ?? '')}`).join('; ');
  }

  const mode = normalizeRequestBodyMode(request);
  const { body, multipart, contentType } = assembleRequestBody(
    {
      method,
      bodyType: mode.bodyType,
      rawBodyLanguage: mode.rawBodyLanguage,
      body: request?.body,
      formDataFields: request?.formDataFields,
    },
    { substituteVariables: sub }
  );
  if (contentType) headers['Content-Type'] = contentType;

  // Params: `enabled` must be true (not merely "not false") — both old call
  // sites agreed on that, and a half-typed row has no `enabled` yet.
  const params = (Array.isArray(request?.params) ? request.params : [])
    .filter((p) => p && p.key && p.enabled)
    .map((p) => ({ key: sub(p.key), value: sub(p.value ?? ''), enabled: true }));
  const url = buildRequestUrl(sub(request?.url || ''), params);

  return { method, url, headers, body, multipart };
}

/** request → nearest folder with auth → collection. Never returns `inherit`. */
export function resolveRequestAuth(requestAuth, { folderAuthChain = [], collectionAuth = null } = {}) {
  return resolveEffectiveAuthChain(requestAuth, folderAuthChain || [], collectionAuth);
}

/** Apply the resolved auth to already-assembled parts. */
export async function authorizeRequestParts(parts, { auth, substituteVariables = identity }) {
  const { headers, url } = await applyRequestAuth({
    auth,
    headers: parts.headers,
    url: parts.url,
    substituteVariables,
    method: parts.method,
    body: parts.body,
  });
  return { ...parts, headers, url };
}

/** The whole thing, for callers with nothing to do between the steps (the runner). */
export async function assembleHttpRequest(
  request,
  { substituteVariables = identity, folderAuthChain = [], collectionAuth = null } = {}
) {
  const parts = assembleRequestParts(request, { substituteVariables });
  const auth = resolveRequestAuth(request?.auth, { folderAuthChain, collectionAuth });
  return authorizeRequestParts(parts, { auth, substituteVariables });
}
